import React from 'react'
import { Link } from 'react-router-dom'
import { Instagram, Mail, Phone, ArrowRight, MapPin, ExternalLink } from 'lucide-react'
import noodLogoWhite from '../assets/images/nood-logo-white.webp'
import { PHONE_NUMBER, PHONE_DISPLAY } from '../config/contact'

const Footer: React.FC = () => {
  return (
    <footer className="relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 hero-pattern opacity-5"></div>
      
      {/* Top curved edge */}
      <div className="absolute top-0 left-0 right-0 h-8 bg-white" style={{ borderRadius: '0 0 50% 50% / 100px' }}></div>

      <div className="relative bg-gradient-to-br from-[var(--color-dark)] to-[#112318] pt-16 text-white">
        {/* Email signup with accent background */}
      
        {/* Main Footer Content */}
        <div className="content-container pb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">
            {/* Logo and brand info */}
            <div className="lg:col-span-4 space-y-6">
              <Link to="/" className="inline-block">
                <img src={noodLogoWhite} alt="Nood" className="h-16 w-auto" />
              </Link>
              <p className="text-white/70 leading-relaxed">
                Empowering Moroccan youth to transform their skills into successful careers through expert-led courses and a supportive community.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/nood.community/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-3 bg-white/10 rounded-full hover:bg-primary transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href="https://linkedin.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-3 bg-white/10 rounded-full hover:bg-primary transition-colors"
                  aria-label="LinkedIn"
                >
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>

            {/* Navigation Columns */}
            <div className="lg:col-span-2 space-y-6">
              <h3 className="text-lg font-semibold">Academy</h3>
              <ul className="space-y-3">
                {['Courses', 'Community', 'Workshops'].map((item) => (
                  <li key={item}>
                    <Link 
                      to={`/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-white/70 hover:text-white transition-colors inline-flex items-center group"
                    >
                      <span className="group-hover:translate-x-1 transition-transform">{item}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <h3 className="text-lg font-semibold">Company</h3>
              <ul className="space-y-3">
                {['Contact', 'Careers', 'Blog'].map((item) => (
                  <li key={item}>
                    <Link 
                      to={`/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-white/70 hover:text-white transition-colors inline-flex items-center group"
                    >
                      <span className="group-hover:translate-x-1 transition-transform">{item}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-4 space-y-6">
              <h3 className="text-lg font-semibold">Contact Us</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Phone size={18} className="text-primary mt-1" />
                  <div>
                    <p className="text-sm text-white/60">Phone</p>
                    <a href={`tel:${PHONE_NUMBER}`} className="text-white/90 hover:text-white transition-colors">
                      {PHONE_DISPLAY}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-primary mt-1" />
                  <div>
                    <p className="text-sm text-white/60">Email</p>
                    <a href="mailto:nood.academy@gmail.com" className="text-white/90 hover:text-white transition-colors">
                      nood.academy@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-primary mt-1" />
                  <div>
                    <p className="text-sm text-white/60">Location</p>
                    <address className="text-white/90 not-italic">
                    Rue Al Araar, Casablanca 20250, Morocco
                    </address>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-white/10">
          <div className="content-container py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
              <div className="flex flex-wrap gap-6 justify-center md:justify-start">
                <Link to="/privacy-policy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms-and-conditions" className="hover:text-white transition-colors">
                  Terms of Service
                </Link>
                <Link to="/cookie-policy" className="hover:text-white transition-colors">
                  Cookie Policy
                </Link>
              </div>
              
              <div>
                <p>RC: 559737 | TP: 3160177 | IF: 52699250 | ICE: 003156748000055</p>
              </div>
            </div>
            <br/>
            <img
    src="/images/BOX1.jpg"
    alt="Ce site web est sécurisé 3D Secure, Payzone, Visa, Mastercard"
    width={400}
    height={40}
    loading="lazy"
    className="max-w-full h-auto"
  />
            <div className="mt-4 text-center md:text-left text-sm text-white/60">
              &copy; {new Date().getFullYear()} NOOD SARL. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
